import type {
  BrandFormState,
  brandKeywordsBrandReduce,
  BrandLanguageFormState,
  guideLinesBrandReduce,
  StyleGuideBrandReduce,
} from './CreateAndEditNewBrandTypes'
import type { BrandsDetailData } from '@contracts'

export const style_guide: StyleGuideBrandReduce[] = [
  {
    id: 'target_audience',
    label: 'Target audience',
    value: '',
    placeholder: 'Please enter',
    defaultMessage:
      'Describe the target audience for the content, this could include details like age, gender, interests and profession',
    error: undefined,
    characterLimit: undefined,
  },
  {
    id: 'tone_of_voice',
    label: 'Tone of voice',
    value: '',
    placeholder: 'Please enter',
    defaultMessage: '',
    error: undefined,
    characterLimit: 1000,
  },
]

export const brand_keywords: brandKeywordsBrandReduce[] = [
  {
    id: 'priority',
    label: 'Priority',
    keyword: true,
    value: [],
    warning: undefined,
    defaultMessage:
      'Enter keywords that should be prioritised in the content to ensure they are prominently featured e.g in the title',
  },
  {
    id: 'required',
    label: 'Required',
    keyword: true,
    value: [],
    warning: undefined,
    defaultMessage: 'Specify keywords that must be included in the content without exception',
  },
  {
    id: 'optional',
    label: 'Optional',
    keyword: true,
    value: [],
    warning: undefined,
    defaultMessage: 'List keywords that can be included if relevant but are not mandatory',
  },
  {
    id: 'omit',
    label: 'Omit',
    keyword: true,
    value: [],
    warning: undefined,
    defaultMessage: 'Identify keywords that should be excluded from the content entirely',
  },
  {
    id: 'keyphrases_required',
    label: 'Required keyphrases',
    keyword: false,
    value: [],
    warning: undefined,
    defaultMessage: 'Enter keyphrases that must appear in the content exactly as specified',
  },
  {
    id: 'keyphrases_optional',
    label: 'Optional Keyphrases',
    keyword: false,
    value: [],
    warning: undefined,
    defaultMessage: 'List keyphrases that may be included in the content if appropriate, but are not required',
  },
]

export const guide_lines: guideLinesBrandReduce[] = [
  {
    id: 'guidelines',
    label: 'Guidelines',
    disclaimer:
      'Provide instructions on how to generate the content. For example, use nouns and adjectives, create informative titles, avoid repetition, and exclude prohibited terms. These can apply to all or specific content types.',
    value: [],
  },
  {
    id: 'guidelines_shortener',
    label: 'Guidelines shortener',
    disclaimer:
      'Provide instructions for shortening content while maintaining key points and clarity. These can be applied to specific content types.',
    value: [],
  },
  {
    id: 'restrictions',
    label: 'Restrictions',
    disclaimer:
      'Specify rules to correct written content, such as ensuring spaces between numbers and units, avoiding all caps in titles, and using numerals. These can be specific to each content type.',
    value: [],
  },
]

export const single_brand_language: BrandLanguageFormState = {
  region: {
    value: 'all',
    error: undefined,
  },
  replacement: {
    value: '',
    error: undefined,
  },
  patterns: {
    value: [],
    error: undefined,
  },
}

// Convert brand data from API to state
export const fromBrandDataToState = (brandData: BrandsDetailData): BrandFormState => {
  const { id, brand_name, brand_language: brandLanguage, brand_keywords: brandKeywords, config } = brandData

  const initialBrandData = {
    id,
    brand_name: {
      value: brand_name,
      error: undefined,
    },
    guide_lines: guide_lines.map(guideLines => ({
      ...guideLines,
      value: config?.content_config?.[guideLines.id as keyof typeof config.content_config] || [],
    })),
    brand_language:
      brandLanguage?.map(({ region, replacement, patterns }) => ({
        region: { value: region, error: undefined },
        replacement: { value: replacement, error: undefined },
        patterns: { value: patterns, error: undefined },
      })) || [],
    brand_keywords: brand_keywords.map(keyword => ({
      ...keyword,
      value: brandKeywords?.[keyword.id as keyof typeof brandKeywords] || [],
    })),
    style_guide: style_guide.map(styleGuide => ({
      ...styleGuide,
      value:
        config?.content_config?.style_guide?.[styleGuide.id as keyof typeof config.content_config.style_guide] || '',
    })),
  } as BrandFormState

  return initialBrandData
}

// scroll to error
export const scrollToError = (errorElement: Element | null) => {
  if (errorElement) {
    errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }
}
